<template>
  <GeojsonLayer
    v-if="sourcePoi"
    :before="before"
    :layer="layerStyle"
    :source="sourcePoi"
    :sourceId="sourceId"
    @layerClicked="onPoiClicked"
    @layerMouseEnter="handleMouseEnterPoi"
    @layerMouseMove="handleMouseMovePoi"
    @layerMouseLeave="handleMouseLeavePoi"
  />
  <GeojsonLayer
    v-if="sourcePoi && sourcePoiSelected"
    :before="layerId"
    :layer="layerStyleSelected"
    :source="sourcePoiSelected"
    :sourceId="sourceIdSelected"
    @layerClicked="onPoiClicked"
    @layerMouseEnter="handleMouseEnterPoi"
    @layerMouseMove="handleMouseMovePoi"
    @layerMouseLeave="handleMouseLeavePoi"
  />
  <div v-show="false">
    <MapPopupNew ref="popup" :rows="[createSmallTitle(popupTitle)]" />
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { useGeneralStore } from '@/pinia'
import { mapState as mapStatePinia } from 'pinia'
import GeojsonLayer from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import MapPopupNew from '../../popups/MapPopupNew.vue'
import { createSmallTitle } from '@/components/Builder'
import { CHANGE_POI } from '@/store/action-types'
import { SET_CLICKED_ITEM_TYPE, SET_POI_MENU } from '@/store/mutation-types'
import { PLANVIEW_PREFIX } from '@/store/module-prefixes'

export default {
  name: 'LayerLevelMarkerPoi',
  components: {
    MapPopupNew,
    GeojsonLayer
  },
  props: {
    before: {
      type: String,
      required: true
    },
    layerId: {
      type: String,
      required: true
    },
    layerIdSelected: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      sourceId: 'source-room-marker-poi',
      sourceIdSelected: 'source-room-marker-poi-selected',
      hoveredPoiId: '',
      popup: new mapboxgl.Popup({ closeButton: false, offset: 15 }),
      popupTitle: '',
      active: false
    }
  },
  computed: {
    ...mapGetters({
      isMeasurementActive: 'isMeasurementActive'
    }),
    ...mapGetters(PLANVIEW_PREFIX, {
      selectedLevelVisiblePois: 'selectedLevelVisiblePois'
    }),
    ...mapState({
      clickedItemType: (state) => state.clickedItemType,
      selectedPoi: (state) => state.selectedPoi
    }),
    ...mapStatePinia(useGeneralStore, ['selectedAttachment', 'selectedTicket']),
    layerStyle() {
      let guid = ''
      // Ivion poi:s don't contain guid
      if (this.selectedPoi) {
        guid = this.selectedPoi.guid ? this.selectedPoi.guid : this.selectedPoi.id
      }
      return {
        id: this.layerId,
        source: this.sourceId,
        type: 'circle',
        maxzoom: 22.1,
        paint: {
          'circle-radius':
            this.clickedItemType === 'poi'
              ? [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  16,
                  ['case', ['==', ['get', 'guid'], guid], 4, 1.75],
                  22,
                  ['case', ['==', ['get', 'guid'], guid], 14, 11]
                ]
              : ['interpolate', ['linear', 1.75], ['zoom'], 16, 1.75, 22, 11],
          'circle-color': ['get', 'color'],
          'circle-stroke-color': [
            'case',
            ['==', ['get', 'guid'], this.selectedAttachmentEntityId],
            'rgba(250, 219, 20, 0.8)',
            ['==', ['get', 'guid'], this.selectedTicketEntityId],
            'rgba(250, 219, 20, 0.8)',
            '#FFF'
          ],
          'circle-stroke-width': [
            'interpolate',
            ['exponential', 1.75],
            ['zoom'],
            4,
            4,
            16,
            [
              'case',
              ['==', ['get', 'guid'], this.selectedAttachmentEntityId],
              8,
              ['==', ['get', 'guid'], this.selectedTicketEntityId],
              8,
              1
            ],
            20,
            [
              'case',
              ['==', ['get', 'guid'], this.selectedAttachmentEntityId],
              16,
              ['==', ['get', 'guid'], this.selectedTicketEntityId],
              16,
              2
            ],
            22,
            [
              'case',
              ['==', ['get', 'guid'], this.selectedAttachmentEntityId],
              22,
              ['==', ['get', 'guid'], this.selectedTicketEntityId],
              22,
              3
            ]
          ],
          'circle-stroke-opacity': [
            'case',
            ['boolean', ['==', ['get', 'guid'], guid], false],
            1,
            0.9
          ],
          'circle-opacity': ['case', ['boolean', ['==', ['get', 'guid'], guid], false], 1, 0.9]
        }
      }
    },
    layerStyleSelected() {
      return {
        id: this.layerIdSelected,
        source: this.sourceIdSelected,
        type: 'circle',
        maxzoom: 22.1,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 16, 4, 22, 11],
          'circle-color': ['get', 'color'],
          'circle-stroke-color': '#6A6A6A',
          'circle-stroke-width': [
            'interpolate',
            ['exponential', 1.75],
            ['zoom'],
            4,
            4,
            20,
            16,
            22,
            22
          ],
          'circle-stroke-opacity': 0.9,
          'circle-opacity': 1
        }
      }
    },
    selectedAttachmentEntityId() {
      return this.selectedAttachment?.entityId ? this.selectedAttachment.entityId : ''
    },
    selectedTicketEntityId() {
      return this.selectedTicket?.entityConnection?.length &&
        this.selectedTicket?.entityConnection[0].id
        ? this.selectedTicket.entityConnection[0].id
        : ''
    },
    sourcePoi() {
      if (!this.selectedLevelVisiblePois.length) return null
      return {
        type: 'geojson',
        data: {
          id: this.sourceId,
          type: 'FeatureCollection',
          features: this.selectedLevelVisiblePois
        }
      }
    },
    sourcePoiSelected() {
      if (!this.selectedLevelVisiblePois.length) return null
      const selectedPoi = []
      if (this.sourcePoi && this.clickedItemType === 'poi') {
        const selectedPoiSource = this.sourcePoi.data.features.find(
          (poi) => poi.properties.guid === this.selectedPoi?.guid
        )
        if (selectedPoiSource) selectedPoi.push(selectedPoiSource)
      }
      return {
        type: 'geojson',
        data: {
          id: this.sourceIdSelected,
          type: 'FeatureCollection',
          features: selectedPoi
        }
      }
    }
  },
  beforeMount() {
    if (this.selectedPoi) {
      if (this.selectedPoi.guid) {
        this.getPoisForLevelPerCategory({
          categoryId: this.selectedPoi.categoryId,
          typeId: this.selectedPoi.typeId,
          checked: true,
          levelId: this.$store.state.selectedLevel.id
        })
      } else {
        this.dispatchPoi({ poi: null })
        this.commitClick({ type: 'level' })
      }
    }
  },
  methods: {
    createSmallTitle,
    ...mapActions({
      dispatchPoi: CHANGE_POI
    }),
    ...mapActions(PLANVIEW_PREFIX, {
      getPoisForLevelPerCategory: 'getPoisForLevelPerCategory'
    }),
    ...mapMutations({
      commitClick: SET_CLICKED_ITEM_TYPE,
      setShowPoiMenu: SET_POI_MENU
    }),
    handleMouseEnterPoi() {
      if (!this.isMeasurementActive) {
        this.setCursor('pointer')
      }
    },
    handleMouseMovePoi(event) {
      if (!this.isMeasurementActive) {
        if (this.isFirstLayer(event)) {
          this.setCursor('pointer')
          this.updateHoverPoi(event)
        }
      }
    },
    handleMouseLeavePoi(event) {
      if (!this.isMeasurementActive) {
        this.setCursor('')
        this.updateHoverPoi(event)
      }
    },
    isFirstLayer(event) {
      const renderedFeatures = this.$store.map.queryRenderedFeatures(event.point)
      if (!renderedFeatures || !renderedFeatures.length) return false
      return (
        renderedFeatures[0].layer.id === this.layerId ||
        renderedFeatures[0].layer.id === this.layerIdSelected
      )
    },
    setCursor(cursorType) {
      this.$store.map.getCanvas().style.cursor = cursorType
    },
    showPopup(coordinates) {
      if (!this.$refs.popup) return
      this.popup.setLngLat(coordinates).setDOMContent(this.$refs.popup.$el).addTo(this.$store.map)
    },
    onPoiClicked(event) {
      this.setShowPoiMenu(false)

      if (this.isMeasurementActive || !this.isFirstLayer(event)) return
      const renderedFeatures = this.$store.map.queryRenderedFeatures(event.point)
      const poi = renderedFeatures.find(
        (feature) =>
          feature.source === this.sourcePoi.data.id ||
          feature.source === this.sourcePoiSelected.data.id
      )
      if (!poi) return
      this.dispatchPoi({
        poi: poi.properties
      })
      this.commitClick({ type: 'poi' })
    },
    updateHoverPoi(event) {
      const features = event.features
      if (features && features.length > 0) {
        const foundPoi = features.find(
          (feature) =>
            feature.source === this.sourcePoi.data.id ||
            feature.source === this.sourcePoiSelected.data.id
        )
        if (!foundPoi) {
          this.popup.remove()
          return
        }
        if (this.hoveredPoiId !== foundPoi.id) {
          this.popupTitle = foundPoi.properties.name
          this.showPopup(foundPoi.geometry.coordinates)
        }
        this.hoveredPoiId = foundPoi.id
      } else {
        this.hoveredPoiId = ''
        this.popup.remove()
      }
    }
  }
}
</script>
